import React, { useState, useEffect } from "react"
import { get } from "../../utils/apiClient"
import Pagination from "../common/pagination"
import moment from "moment-timezone"
import { formatFaxNumber, sortData } from "../../utils/common"
import Select from "react-select"
import SearchInputWrapper from "../common/searchInputWrapper"
import { ArrowUp, ArrowDown } from "lucide-react"

const tabs = [
  {
    key: "deleteReports",
    label: "Deleted Faxes Log",
  },
]

const Reports = ({ queueLevelData }) => {
  const [tab, setTab] = useState("deleteReports")
  const [reportData, setReportData] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [searchText, setSearchText] = useState("")
  const [selectedFaxType, setSelectedFaxType] = useState(null)
  const [selectedQueueName, setSelectedQueueName] = useState(null)
  const [selectedDeletedBy, setSelectedDeletedBy] = useState(null)
  const [sortConfig, setSortConfig] = useState({
    key: "deletedOn",
    direction: "desc",
  })

  const fetchSettings = async () => {
    setLoading(true)
    try {
      const data = await get(`/admin/deleted-fax-report`)
      setReportData(data)
      setLoading(false)
    } catch (error) {
      console.error("Failed to fetch setting", error)
      setLoading(false)
    }
  }

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }))
  }

  useEffect(() => {
    fetchSettings()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Search Filter
  let filteredReportData = reportData?.filter((queue) => {
    return Object.entries(queue).some(([key, value]) => {
      if (key === "deletedOn") {
        return moment
          .unix(value)
          .tz("America/New_York")
          .format("MM/DD/YYYY hh:mm A")
          ?.toString()
          .toLowerCase()
          .includes(searchText.toLowerCase())
      }

      // For other fields, apply standard filtering
      if (
        key === "faxId" ||
        key === "faxNumber" ||
        key === "originatingFaxNumber" ||
        key === "queueName" ||
        key === "deletedBy"
      ) {
        return value
          ?.toString()
          .toLowerCase()
          .includes(searchText.toLowerCase())
      }

      return false
    })
  })

  if (selectedFaxType) {
    filteredReportData = filteredReportData.filter(
      (rec) => rec.faxType === selectedFaxType.value,
    )
  }

  if (selectedQueueName) {
    filteredReportData = filteredReportData.filter(
      (rec) => rec.queueName === selectedQueueName.value,
    )
  }

  if (selectedDeletedBy) {
    filteredReportData = filteredReportData.filter(
      (rec) => rec.deletedBy === selectedDeletedBy.value,
    )
  }

  filteredReportData = sortData(filteredReportData, sortConfig)

  const startIdx = (currentPage - 1) * rowsPerPage
  const currentReportData = filteredReportData.slice(
    startIdx,
    startIdx + rowsPerPage,
  )
  const totalPages = Math.ceil(filteredReportData.length / rowsPerPage)

  const faxTypeOptions = [
    ...new Set(
      reportData
        .map((r) => r.faxType)
        .filter((q) => typeof q === "string" && q.trim().length > 0),
    ),
  ]
    .map((q) => ({
      value: q,
      label: q,
    }))
    .sort((a, b) => a.value.localeCompare(b.value))

  const queueNameOptions = [
    ...new Set(
      reportData
        .map((r) => r.queueName)
        .filter((q) => typeof q === "string" && q.trim().length > 0),
    ),
  ]
    .map((q) => ({
      value: q,
      label: q,
    }))
    .sort((a, b) => a.value.localeCompare(b.value))

  const deletedByOptions = [...new Set(reportData.map((r) => r.deletedBy))]
    .map((q) => ({
      value: q,
      label: q,
    }))
    .sort((a, b) => a.value.localeCompare(b.value))

  const handleExportCsv = () => {
    // CSV header
    const headers = [
      "Fax Id",
      "Fax Type",
      "Originating Fax Number",
      "Queue Name",
      "Queue Fax Number",
      "Deleted By",
      "Deleted On",
    ]
    // Build rows
    const rows = currentReportData.map((reportdata) => [
      reportdata.faxId,
      reportdata.faxType,
      formatFaxNumber(reportdata.originatingFaxNumber),
      reportdata.queueName,
      formatFaxNumber(reportdata.faxNumber),
      reportdata.deletedBy,
      moment
        .unix(reportdata.deletedOn)
        .tz("America/New_York")
        .format("MM/DD/YYYY hh:mm A"),
    ])
    // Assemble CSV string
    const csvContent = [headers, ...rows]
      .map((row) =>
        row
          .map(
            (cell) =>
              // escape quotes
              `"${String(cell).replace(/"/g, '""')}"`,
          )
          .join(","),
      )
      .join("\n")
    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `DeletedFaxesLog.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <div className="faxes-content">
      {loading ? (
        <div className="loading-container-fax-list">
          <div className="loading-message">Loading...</div>
        </div>
      ) : (
        <>
          <div
            className="fax-view-tabs"
            style={{ width: "300px", border: "none", margin: "20px 0px" }}
          >
            {tabs.map((t) => (
              <div
                key={t.key}
                className={`settings-tab-right-tabs ${
                  tab === t.key ? "selected" : ""
                }`}
                onClick={() => setTab(t.key)}
              >
                {t.label}
              </div>
            ))}
          </div>

          <div
            className="search-container"
            style={{
              width: "100%",
              alignItems: "end",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <div
              className="setting-note"
              style={{ display: "flex", gap: "5px", fontWeight: "normal" }}
            >
              <div>
                <SearchInputWrapper
                  placeholder={`Search...`}
                  value={searchText}
                  onChange={(val) => {
                    setSearchText(val)
                    setCurrentPage(1)
                  }}
                  onClear={() => {
                    setSearchText("")
                    setCurrentPage(1)
                  }}
                />
              </div>

              <Select
                className="basic-single"
                placeholder="Filter by FaxType"
                options={faxTypeOptions}
                value={selectedFaxType}
                onChange={(v) => {
                  setSelectedFaxType(v)
                  setCurrentPage(1)
                }}
                isClearable
                isSearchable
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: 0,
                    minWidth: 100,
                    height: 41,
                  }),
                }}
              />

              <Select
                className="basic-single"
                placeholder="Filter by Queue/NetId"
                options={queueNameOptions}
                value={selectedQueueName}
                onChange={(v) => {
                  setSelectedQueueName(v)
                  setCurrentPage(1)
                }}
                isClearable
                //isSearchable
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: 0,
                    minWidth: 200,
                    height: 41,
                  }),
                }}
              />

              <Select
                className="basic-single"
                placeholder="Filter by DeletedBy"
                options={deletedByOptions}
                value={selectedDeletedBy}
                // onChange={setSelectedDeletedBy}
                onChange={(v) => {
                  setSelectedDeletedBy(v)
                  setCurrentPage(1)
                }}
                isClearable
                isSearchable
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: 0,
                    minWidth: 250,
                    height: 41,
                  }),
                }}
              />

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  marginLeft: "6px",
                }}
                onClick={() => {
                  setSearchText("")
                  setSelectedDeletedBy(null)
                  setSelectedQueueName(null)
                }}
                title="Reset"
              >
                {" "}
                ↺
              </div>
            </div>
          </div>
          <div className="table-container">
            <table className="faxes-table">
              <thead>
                <tr>
                  <th className="faxes-th">Fax Id</th>
                  <th className="faxes-th">Fax Type</th>
                  <th className="faxes-th">Originating Fax Number</th>
                  <th className="faxes-th">Queue Name</th>
                  <th className="faxes-th">Queue Fax Number</th>
                  <th className="faxes-th">Deleted By</th>
                  <th className="faxes-th">
                    Deleted On
                    <span
                      style={{ marginLeft: "5px", cursor: "pointer" }}
                      onClick={() => {
                        handleSort("deletedOn")
                      }}
                      title={sortConfig.direction}
                    >
                      {sortConfig.direction === "asc" ? (
                        <ArrowUp size={14} color="grey" />
                      ) : (
                        <ArrowDown size={14} color="grey" />
                      )}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentReportData.length > 0 ? (
                  currentReportData?.map((reportdata) => (
                    <tr key={reportdata.reportdata_id} className="fax-item">
                      <td className="faxes-td">{reportdata.faxId}</td>
                      <td className="faxes-td">{reportdata.faxType}</td>

                      <td className="faxes-td">
                        {formatFaxNumber(reportdata?.originatingFaxNumber)}
                      </td>

                      <td className="faxes-td">{reportdata.queueName}</td>

                      <td className="faxes-td">
                        {formatFaxNumber(reportdata.faxNumber)}
                      </td>

                      <td className="faxes-td">{reportdata.deletedBy}</td>
                      <td className="faxes-td">
                        {moment
                          .unix(reportdata.deletedOn)
                          .tz("America/New_York")
                          .format("MM/DD/YYYY hh:mm A")}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="100%"
                      style={{
                        textAlign: "center",
                        padding: "10px",
                        border: "1px solid #ccc",
                      }}
                    >
                      No data found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div
            className="faxes-pagination"
            style={{
              marginTop: "5px",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            {currentReportData.length > 0 && (
              <div>
                <button
                  className="faxes-button"
                  onClick={handleExportCsv}
                  style={{ marginLeft: "0px" }}
                >
                  Export to CSV
                </button>
              </div>
            )}
            {currentReportData.length > 0 && (
              <div
                className="faxes-pagination"
                style={{
                  marginTop: "8px",
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                {currentReportData.length > 0 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    rowsPerPage={rowsPerPage}
                    totalRows={currentReportData.length}
                    onPageChange={(page) => setCurrentPage(page)}
                    onRowsPerPageChange={(count) => {
                      setRowsPerPage(count)
                      setCurrentPage(1) // Reset to first page on change
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default Reports
